import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Clock, Phone, Target, ThumbsUp, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getCallDaily,
  getCallStats,
  type CallDailyPeriod,
  type CallDailyResponse,
  type CallPeriodStats,
  type CallStatsResponse,
} from "@/api/callCenter";
import { usePermissions } from "@/hooks/usePermissions";
import { ALL_MEMBERS, CallUserFilter } from "./CallUserFilter";

interface CallStatsTabProps {
  slug: string;
}

/** Performance dashboard for the operator — KPI cards, daily volume,
 *  outcome breakdown, weekly interest-rate trend, hourly heatmap.
 *  All charts gracefully degrade to an empty state if the backend
 *  analytics endpoints haven't shipped (getCallStats returns null). */
export function CallStatsTab({ slug }: CallStatsTabProps) {
  const { canViewCallCenterTeam } = usePermissions();
  const [period, setPeriod] = useState<CallDailyPeriod>("month");
  const [selectedUserId, setSelectedUserId] = useState(ALL_MEMBERS);
  const [stats, setStats] = useState<CallStatsResponse | null>(null);
  const [daily, setDaily] = useState<CallDailyResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Only privileged users may scope to another operator; for everyone
  // else we send no user_id and the backend pins results to them.
  const userIdParam = canViewCallCenterTeam ? selectedUserId : undefined;

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    Promise.all([
      getCallStats(slug, period, userIdParam),
      getCallDaily(slug, period, userIdParam),
    ]).then(([s, d]) => {
      if (cancelled) return;
      setStats(s);
      setDaily(d);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, period, userIdParam]);

  // Backend returns every bucket; we pick the active period so the
  // tab switcher actually changes the numbers (the top-level mirror
  // is `month`-only and would make week/quarter no-ops).
  const bucket: CallPeriodStats | null = stats ? stats[period] : null;
  const reachPct = bucket?.reach_rate ?? 0;
  const talkMin = bucket?.talk_minutes ?? 0;
  const talkH = Math.floor(talkMin / 60);
  const talkM = talkMin % 60;
  const interestedPct = useMemo(() => {
    if (!bucket || bucket.calls === 0) return undefined;
    return Math.round((bucket.interested / bucket.calls) * 100);
  }, [bucket]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Performance</h2>
        <div className="flex items-center gap-3">
          {canViewCallCenterTeam && (
            <CallUserFilter
              slug={slug}
              value={selectedUserId}
              onChange={setSelectedUserId}
            />
          )}
          <PeriodTabs period={period} onChange={setPeriod} />
        </div>
      </div>

      {loaded && !stats && (
        <div className="surface-plane p-8 text-center">
          <p className="text-sm text-slate-600">
            Call analytics aren&rsquo;t available yet for this project.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Once the backend rolls out the call-stats endpoint, this page
            will populate.
          </p>
        </div>
      )}

      {stats && bucket && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <KpiCard
              icon={<Phone className="h-5 w-5 text-indigo-500" />}
              label="Total Calls"
              value={bucket.calls}
            />
            <KpiCard
              icon={<ThumbsUp className="h-5 w-5 text-emerald-500" />}
              label="Interested"
              value={bucket.interested}
              subtitle={
                interestedPct != null ? `${interestedPct}% rate` : undefined
              }
            />
            <KpiCard
              icon={<Clock className="h-5 w-5 text-amber-500" />}
              label="Talk Time"
              value={`${talkH}h ${talkM}m`}
            />
            <KpiCard
              icon={<Target className="h-5 w-5 text-violet-500" />}
              label="Reach Rate"
              value={`${reachPct}%`}
            />
          </div>

          {stats.streak != null && stats.streak >= 3 && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
              <span className="text-3xl">🔥</span>
              <div>
                <span className="font-bold text-amber-800">
                  {stats.streak}-day streak!
                </span>
                <p className="text-xs text-amber-600">
                  You&rsquo;ve been calling consistently, keep it up!
                </p>
              </div>
            </div>
          )}

          {stats.badges && stats.badges.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {stats.badges.map((badge, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700"
                >
                  {badge.icon} {badge.label}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="surface-plane p-4">
              <h3 className="mb-3 text-sm font-medium text-slate-700">
                Calls per Day
              </h3>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily?.daily ?? []}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f3f4f6"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickFormatter={(d) =>
                        new Date(d).toLocaleDateString("fr", {
                          weekday: "short",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Bar
                      dataKey="calls"
                      fill="#6366f1"
                      radius={[4, 4, 0, 0]}
                      name="Calls"
                    />
                    <Bar
                      dataKey="interested"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      name="Interested"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="surface-plane p-4">
              <h3 className="mb-3 text-sm font-medium text-slate-700">
                Outcomes
              </h3>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "Interested",
                          value: bucket.interested,
                          fill: "#10b981",
                        },
                        {
                          name: "Neutral",
                          value: bucket.neutral,
                          fill: "#f59e0b",
                        },
                        {
                          name: "Not Interested",
                          value: bucket.not_interested,
                          fill: "#f43f5e",
                        },
                        {
                          name: "No Answer",
                          value: bucket.no_answer,
                          fill: "#9ca3af",
                        },
                      ].filter((d) => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    />

                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="surface-plane p-4">
            <h3 className="mb-3 text-sm font-medium text-slate-700">
              Interest Rate Trend
            </h3>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily?.weekly_rates ?? []}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f3f4f6"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    formatter={(v: number) => [`${v}%`, "Rate"]}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="interest_rate"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#10b981" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="reach_rate"
                    stroke="#6366f1"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: "#6366f1" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex items-center justify-center gap-6 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <span className="h-0.5 w-3 rounded bg-emerald-500" /> Interest
                rate
              </span>
              <span className="flex items-center gap-1">
                <span className="h-0.5 w-3 rounded border-dashed bg-indigo-500" />{" "}
                Reach rate
              </span>
            </div>
          </div>

          <div className="surface-plane p-4">
            <h3 className="mb-3 text-sm font-medium text-slate-700">
              Best Hours to Call
            </h3>
            <div className="grid grid-cols-12 gap-1">
              {(daily?.hourly ?? []).map((h, i) => {
                const intensity =
                  h.calls > 0 ? Math.min(1, h.interested / h.calls) : 0;
                return (
                  <div key={i} className="text-center">
                    <div
                      title={`${h.hour}h: ${h.calls} calls, ${h.interested} interested`}
                      className={cn(
                        "h-8 rounded-md",
                        intensity > 0.3
                          ? "bg-emerald-400"
                          : intensity > 0.1
                            ? "bg-emerald-200"
                            : h.calls > 0
                              ? "bg-slate-200"
                              : "bg-slate-50",
                      )}
                    />
                    <span className="text-[9px] text-slate-400">{h.hour}h</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-center text-[10px] text-slate-400">
              Darker = higher interest rate
            </p>
          </div>

          {stats.personal_record && (
            <div className="rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 p-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6 text-indigo-500" />
                <div>
                  <span className="text-sm font-medium text-indigo-800">
                    Personal Record
                  </span>
                  <p className="text-xs text-indigo-600">
                    {stats.personal_record.calls} calls on{" "}
                    {new Date(stats.personal_record.date).toLocaleDateString(
                      "fr",
                      {
                        day: "numeric",
                        month: "long",
                      },
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PeriodTabs({
  period,
  onChange,
}: {
  period: CallDailyPeriod;
  onChange: (next: CallDailyPeriod) => void;
}) {
  const options: Array<{ value: CallDailyPeriod; label: string }> = [
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "quarter", label: "Quarter" },
  ];
  return (
    <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
      {options.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            period === p.value
              ? "bg-white text-slate-900 shadow-sm-tile"
              : "text-slate-500 hover:text-slate-700",
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <div className="surface-plane p-4">
      <div className="mb-2 flex items-center justify-between">{icon}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500">{label}</div>
      {subtitle && (
        <div className="mt-0.5 text-[10px] text-slate-400">{subtitle}</div>
      )}
    </div>
  );
}
